import type { HabitWithLog } from '../interfaces'

type SubHabitProgressAction =
  | { type: 'complete'; logId: string }
  | { type: 'undo' }

function getSubHabitWeeklyTargetMet(subHabit: HabitWithLog): boolean {
  return (
    subHabit.frequency_type === 'x_per_week' &&
    subHabit.weeklyCompletions >= (subHabit.frequency_target ?? 1)
  )
}

function updateSubHabitProgress(
  subHabit: HabitWithLog,
  targetSubHabitId: string,
  action: SubHabitProgressAction,
): HabitWithLog {
  if (subHabit.id !== targetSubHabitId) return subHabit

  if (action.type === 'undo') {
    const weeklyCompletions =
      subHabit.logId === null
        ? subHabit.weeklyCompletions
        : Math.max(0, subHabit.weeklyCompletions - 1)
    const updatedSubHabit = {
      ...subHabit,
      logId: null,
      weeklyCompletions,
    }
    const isWeeklyTargetMet = getSubHabitWeeklyTargetMet(updatedSubHabit)

    return {
      ...updatedSubHabit,
      isCompletedThisPeriod: false,
      isWeeklyTargetMet,
    }
  }

  const alreadyCountedToday = subHabit.logId !== null
  const weeklyCompletions = alreadyCountedToday
    ? subHabit.weeklyCompletions
    : subHabit.weeklyCompletions + 1
  const updatedSubHabit = {
    ...subHabit,
    isCompletedThisPeriod: true,
    isSkippedThisPeriod: false,
    logId: action.logId,
    weeklyCompletions,
  }
  const isWeeklyTargetMet = getSubHabitWeeklyTargetMet(updatedSubHabit)

  return {
    ...updatedSubHabit,
    isWeeklyTargetMet,
  }
}

export function updateParentWithSubHabitProgress(
  habits: HabitWithLog[],
  parentId: string,
  subHabitId: string,
  action: SubHabitProgressAction,
): HabitWithLog[] {
  return habits.map((parent) => {
    if (parent.id !== parentId) return parent

    const updatedSubHabits = (parent.subHabits ?? []).map((subHabit) =>
      updateSubHabitProgress(subHabit, subHabitId, action),
    )
    const weeklyCompletions = updatedSubHabits.reduce(
      (sum, subHabit) => sum + subHabit.weeklyCompletions,
      0,
    )
    const isParentDoneThisPeriod =
      updatedSubHabits.length > 0 &&
      updatedSubHabits.every(
        (sh) => sh.logId !== null && !sh.isSkippedThisPeriod,
      )
    const isParentWeeklyTargetMet =
      weeklyCompletions >= (parent.frequency_target ?? 1)

    return {
      ...parent,
      subHabits: updatedSubHabits,
      weeklyCompletions,
      isCompletedThisPeriod: isParentDoneThisPeriod,
      isWeeklyTargetMet: isParentWeeklyTargetMet,
    }
  })
}
